/* =====================================================================================
   PDP v2 behaviours — bossman.com  (YourBrandFuel, 2026-09-02)

   Loaded as a module by sections/main-product-v2.liquid. No jQuery, no globals.

   Listens to the theme's own events and never re-implements them:
     variant:change  (form)      -> price row, badges, stock, tier tiles, upgrade line,
                                    scent disclosure, spec table, shipping nudge
     variant:add     (form)      -> the "Added" moment on every submit button of the form
     cart:change     (document)  -> shipping nudge re-computed from the live cart total

   Data comes from Liquid-rendered JSON already on the page:
     [data-pdp-variant-map]  the product's variants with inventory (the theme's product
                             JSON has no inventory numbers)
     [data-pdp-spec-map]     arena.label / arena.value rows per variant
     #scent-data             the scent copy map an existing custom-liquid section emits
   ===================================================================================== */

const parseJson = (el) => {
  if (!el) return null;
  try { return JSON.parse(el.textContent); } catch (e) { return null; }
};

function formatMoney(cents, format) {
  const value = Number(cents) || 0;
  const fmt = format || '${{amount}}';
  const withSep = (num, decimals, thousands, decimal) => {
    const fixed = (num / 100).toFixed(decimals);
    const [int, frac] = fixed.split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac !== undefined ? grouped + decimal + frac : grouped;
  };
  let out;
  if (fmt.includes('amount_no_decimals_with_comma_separator')) out = withSep(value, 0, '.', ',');
  else if (fmt.includes('amount_with_comma_separator')) out = withSep(value, 2, '.', ',');
  else if (fmt.includes('amount_no_decimals')) out = withSep(value, 0, ',', '.');
  else out = withSep(value, 2, ',', '.');
  return fmt.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, out).replace(/<[^>]+>/g, '').trim();
}

const routeRoot = () => (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/');

/* one /cart.js read shared by the column (shipping nudge) and the cross-sell (dedupe) */
let cartPromise = null;
const getCart = (force) => {
  if (!cartPromise || force) {
    cartPromise = fetch(routeRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null);
  }
  return cartPromise;
};

const replay = (el, className) => {
  el.classList.remove(className);
  void el.offsetWidth; /* restart the CSS animation */
  el.classList.add(className);
};

const ADDED_HTML = 'Added <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true" style="vertical-align:-2px"><path d="M3 8.5l3 3 7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

class PdpBuyColumn {
  constructor(root) {
    this.root = root;
    this.formId = root.dataset.formId;
    this.form = document.getElementById(this.formId);
    this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';

    this.variants = parseJson(root.querySelector('[data-pdp-variant-map]')) || [];
    this.byId = new Map(this.variants.map((v) => [String(v.id), v]));
    this.specMap = parseJson(root.querySelector('[data-pdp-spec-map]'));
    this.scentData = parseJson(document.getElementById('scent-data'));

    this.qtyInput = document.getElementById(this.formId + '-quantity');
    this.shipEl = root.querySelector('[data-pdp-ship]');
    this.cartTotal = Number(this.shipEl?.dataset.cartTotal || 0);
    this.upgradeEl = root.querySelector('[data-pdp-upgrade]');
    this.upgradeTarget = null;
    this.lastVariant = null;

    this.bind();
    this.render(this.currentVariant());
  }

  /* ---------- wiring ---------- */

  bind() {
    if (this.form) {
      this.form.addEventListener('variant:change', (event) => this.render(event.detail.variant));
      this.form.addEventListener('variant:add', () => this.addedMoment());
    }

    document.addEventListener('cart:change', (event) => {
      const cart = event.detail && event.detail.cart;
      if (cart && typeof cart.total_price === 'number') {
        this.cartTotal = cart.total_price;
        this.renderShipping(this.lastVariant);
      }
    });

    if (this.qtyInput) {
      ['change', 'input'].forEach((name) => this.qtyInput.addEventListener(name, () => this.renderShipping(this.lastVariant)));
    }

    if (this.upgradeEl) this.upgradeEl.addEventListener('click', () => this.applyUpgrade());

    this.watchSubscription();
    this.refreshCart();
  }

  currentVariant() {
    const idField = this.form && this.form.elements && this.form.elements.id;
    const id = idField ? idField.value : null;
    return this.byId.get(String(id)) || this.variants.find((v) => v.available) || this.variants[0] || null;
  }

  money(cents) {
    return formatMoney(cents, this.moneyFormat);
  }

  /* ---------- the render pass ---------- */

  render(variant) {
    if (!variant) return;
    const v = this.byId.get(String(variant.id)) || variant;
    this.lastVariant = v;

    this.renderPrice(v);
    this.renderBadges(v);
    this.renderStock(v);
    this.renderOptionValues();
    this.renderSwatches();
    this.renderTiers(v);
    this.renderUpgrade(v);
    this.renderScent();
    this.renderSpec(v);
    this.renderShipping(v);
    this.renderStickyBar(v);
    /* Recharge repaints its own prices after the variant change, so re-read them on the next tick */
    window.setTimeout(() => this.syncSubscriptionBenefit(), 400);
  }

  renderPrice(v) {
    const price = this.root.querySelector('[data-pdp-price]');
    const compare = this.root.querySelector('[data-pdp-compare]');
    const save = this.root.querySelector('[data-pdp-save]');
    if (price) price.textContent = this.money(v.price);
    const onSale = Number(v.compare_at_price) > Number(v.price);
    if (compare) {
      compare.hidden = !onSale;
      if (onSale) compare.textContent = this.money(v.compare_at_price);
    }
    if (save) {
      save.hidden = !onSale;
      if (onSale) save.textContent = 'Save ' + Math.round((v.compare_at_price - v.price) / v.compare_at_price * 100) + '%';
    }
  }

  isLow(v, threshold) {
    return Boolean(v.available && v.inventory_management && v.inventory_policy === 'deny' && threshold > 0 && v.inventory_quantity <= threshold);
  }

  renderBadges(v) {
    const row = this.root.querySelector('[data-pdp-badges]');
    if (!row) return;
    const threshold = Number(row.dataset.threshold || 0);
    const low = row.querySelector('[data-badge="low"]');
    const out = row.querySelector('[data-badge="out"]');
    if (low) {
      const isLow = this.isLow(v, threshold);
      low.hidden = !isLow;
      if (isLow) low.textContent = (row.dataset.lowTemplate || 'Only {count} left').replace('{count}', v.inventory_quantity);
    }
    if (out) out.hidden = Boolean(v.available);
  }

  renderStock(v) {
    const el = this.root.querySelector('[data-pdp-stock]');
    if (!el) return;
    const threshold = Number(el.dataset.threshold || 0);
    const state = !v.available ? 'out' : this.isLow(v, threshold) ? 'low' : 'in';
    const changed = el.dataset.state !== state;
    el.dataset.state = state;
    el.querySelectorAll('[data-stock]').forEach((span) => { span.hidden = span.dataset.stock !== state; });
    const count = el.querySelector('[data-stock-count]');
    if (count && state === 'low') count.textContent = v.inventory_quantity;
    if (changed) this.replay(el, 'is-updated');
  }

  renderOptionValues() {
    this.root.querySelectorAll('[data-pdp-option]').forEach((fieldset) => {
      const label = fieldset.querySelector('[data-pdp-option-value]');
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (label && checked) label.textContent = checked.value;
    });
  }

  /* Swatch icons follow the other options: with Single selected each scent shows its single can, with 3-Pack it
     shows that scent's 3-pack. Liquid gets the first paint right (pdp-variant-picker.liquid applies the same
     shared-photo fallback as below); this keeps it right as the size changes.

     The guard matters. Several products share ONE variety-pack photo across every scent's 6-pack, so a blind swap
     would render seven identical swatches and the scent row would stop being a choice. When the candidates are not
     distinct, each scent falls back to its own first variant photo. */
  renderSwatches() {
    const selected = this.selectedOptions();

    this.root.querySelectorAll('[data-pdp-option][data-kind="swatch"]').forEach((fieldset) => {
      const position = Number(fieldset.dataset.optionPosition);
      const swatches = Array.from(fieldset.querySelectorAll('.pdp-swatch'));
      if (swatches.length < 2) return;

      /* enough of the values must resolve to their OWN photo for the row to still read as a choice */
      const enough = Math.ceil(swatches.length * 0.75);
      const isDistinct = (list) => new Set(list.filter(Boolean)).size >= enough;

      let candidates = swatches.map((swatch) => {
        const rep = this.representative(position, swatch.dataset.value, selected);
        return rep && rep.image ? rep.image : null;
      });

      if (!isDistinct(candidates)) {
        /* 6 Pack Variety is one shared photo across six scents, which would render six identical swatches.
           Fall back to each scent's own first (single) variant image, which is what identifies the scent. */
        candidates = swatches.map((swatch) => {
          const own = this.variants.find((v) => Array.isArray(v.options) && v.options[position - 1] === swatch.dataset.value && v.image);
          return own ? own.image : null;
        });
        if (!isDistinct(candidates)) return;
      }

      swatches.forEach((swatch, index) => {
        const src = candidates[index];
        const img = swatch.querySelector('.pdp-swatch__img');
        if (!src || !img || img.dataset.pdpSrc === src) return;
        img.dataset.pdpSrc = src;
        /* the Liquid rendered a srcset; it would win over a plain src change */
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = src;
      });
    });
  }

  /* tiles: recompute each tile's representative variant against the other selected options */

  selectedOptions() {
    const out = [];
    this.root.querySelectorAll('[data-pdp-option]').forEach((fieldset) => {
      const pos = Number(fieldset.dataset.optionPosition);
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      out[pos - 1] = checked ? checked.value : null;
    });
    return out;
  }

  representative(position, value, selected) {
    return this.variants.find((v) => v.options.every((val, i) => (i === position - 1 ? val === value : val === selected[i])));
  }

  tierUnits(tile) {
    return Math.max(1, Number(tile.dataset.units) || 1);
  }

  renderTiers() {
    const selected = this.selectedOptions();
    const groups = new Map();

    this.root.querySelectorAll('[data-pdp-tier]').forEach((tile) => {
      const pos = Number(tile.dataset.optionPosition);
      const rep = this.representative(pos, tile.dataset.value, selected);
      tile.dataset.variantId = rep ? rep.id : '';
      if (!groups.has(pos)) groups.set(pos, []);
      groups.get(pos).push({ tile, rep, units: this.tierUnits(tile) });
    });

    groups.forEach((tiles) => {
      const base = tiles.filter((t) => t.rep).sort((a, b) => a.units - b.units)[0];
      const basePerUnit = base ? base.rep.price / base.units : null;

      tiles.forEach(({ tile, rep, units }) => {
        const price = tile.querySelector('[data-tier-price]');
        const unit = tile.querySelector('[data-tier-unit]');
        const save = tile.querySelector('[data-tier-save]');
        if (!rep) {
          if (price) price.textContent = '';
          if (unit) unit.hidden = true;
          if (save) save.hidden = true;
          return;
        }
        const perUnit = rep.price / units;
        if (price) price.textContent = this.money(rep.price);
        if (unit) {
          unit.hidden = units < 2;
          if (units >= 2) unit.textContent = this.money(perUnit) + ' each';
        }
        if (save) {
          let pct = 0;
          if (Number(rep.compare_at_price) > rep.price) {
            pct = Math.round((rep.compare_at_price - rep.price) / rep.compare_at_price * 100);
          } else if (basePerUnit && units > base.units && perUnit < basePerUnit) {
            pct = Math.round((1 - perUnit / basePerUnit) * 100);
          }
          save.hidden = pct < 1;
          if (pct >= 1) save.textContent = 'Save ' + pct + '%';
        }
      });
    });
  }

  /* upgrade line: the next tier up with a strictly better per-unit than the selected one */

  renderUpgrade() {
    if (!this.upgradeEl) return;
    const selected = this.selectedOptions();
    const tiles = Array.from(this.root.querySelectorAll('[data-pdp-tier]'));
    const current = tiles.find((tile) => tile.querySelector('input[type="radio"]:checked'));
    if (!current) { this.upgradeEl.hidden = true; return; }

    const pos = Number(current.dataset.optionPosition);
    const currentRep = this.representative(pos, current.dataset.value, selected);
    if (!currentRep) { this.upgradeEl.hidden = true; return; }
    const currentUnits = this.tierUnits(current);
    const currentPerUnit = currentRep.price / currentUnits;

    let best = null;
    tiles.forEach((tile) => {
      if (tile === current || Number(tile.dataset.optionPosition) !== pos) return;
      const units = this.tierUnits(tile);
      if (units <= currentUnits) return;
      const rep = this.representative(pos, tile.dataset.value, selected);
      if (!rep || !rep.available) return;
      const perUnit = rep.price / units;
      if (perUnit >= currentPerUnit - 0.5) return; /* half a cent of tolerance */
      if (!best || perUnit < best.perUnit) best = { tile, rep, units, perUnit };
    });

    if (!best) { this.upgradeEl.hidden = true; this.upgradeTarget = null; return; }

    const saving = Math.round(currentPerUnit * best.units - best.rep.price);
    const template = this.upgradeEl.dataset.template || '{tier}: {unit} each, save {save}';
    const text = template
      .replace('{tier}', best.tile.dataset.value)
      .replace('{unit}', this.money(best.perUnit))
      .replace('{save}', this.money(saving))
      .replace('{price}', this.money(best.rep.price));
    const textEl = this.upgradeEl.querySelector('[data-pdp-upgrade-text]');
    if (textEl) textEl.textContent = text;
    this.upgradeTarget = best.tile;
    this.upgradeEl.hidden = false;
  }

  applyUpgrade() {
    const input = this.upgradeTarget && this.upgradeTarget.querySelector('input[type="radio"]');
    if (!input) return;
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* scent disclosure: copy from #scent-data keyed by the selected swatch value */

  renderScent() {
    this.root.querySelectorAll('[data-pdp-scent]').forEach((details) => {
      const fieldset = details.closest('[data-pdp-option]');
      const checked = fieldset && fieldset.querySelector('input[type="radio"]:checked');
      const key = checked ? checked.value : null;
      const data = key && this.scentData ? this.scentData[key] : null;
      if (!data) { details.hidden = true; return; }

      const title = details.querySelector('[data-pdp-scent-title]');
      const text = details.querySelector('[data-pdp-scent-text]');
      const img = details.querySelector('[data-pdp-scent-img]');
      const changed = details.dataset.scentKey !== key;
      details.dataset.scentKey = key;
      if (title) title.textContent = data.title || key;
      if (text) text.textContent = data.description || '';
      if (img) {
        if (data.image) { img.src = data.image; img.alt = data.title || key; img.hidden = false; }
        else { img.hidden = true; img.removeAttribute('src'); }
      }
      details.hidden = false;
      if (changed && details.open) this.replay(details, 'is-swapping');

      /* editor switch: expanded on desktop, collapsed on phones (applied once, the toggle still works) */
      if (details.hasAttribute('data-mobile-collapsed') && !details.dataset.mobileApplied) {
        details.dataset.mobileApplied = '1';
        if (window.matchMedia('(max-width: 699px)').matches) details.open = false;
      }

      /* phone clamp: "More" is a real More/Less toggle that stays rendered (keyboard focus survives),
         offered only when the copy really overflows two lines. The measurement is only meaningful
         while the card is open and laid out, so it re-runs on toggle, on crossing the 700px
         breakpoint (rotation) and once fonts are in. */
      const more = details.querySelector('[data-pdp-scent-more]');
      const measure = () => {
        if (!text || !details.open) return;
        details.classList.toggle('has-more', text.scrollHeight > text.clientHeight + 2);
      };
      if (!details.dataset.scentBound) {
        details.dataset.scentBound = '1';
        details.addEventListener('toggle', measure);
        const phone = window.matchMedia('(max-width: 699px)');
        if (phone.addEventListener) phone.addEventListener('change', measure);
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure).catch(() => {});
        if (more) {
          more.addEventListener('click', () => {
            const open = details.classList.toggle('is-expanded');
            more.setAttribute('aria-expanded', String(open));
            more.textContent = open ? 'Less' : 'More';
          });
        }
      }
      if (changed) {
        details.classList.remove('is-expanded');
        if (more) { more.setAttribute('aria-expanded', 'false'); more.textContent = 'More'; }
      }
      measure();
    });
  }

  /* spec table: rows per variant, product-level fallback, no fetch */

  renderSpec(v) {
    const el = this.root.querySelector('[data-pdp-spec]');
    if (!el || !this.specMap) return;
    const rows = this.specMap[String(v.id)] || this.specMap.default || [];
    const body = el.querySelector('[data-pdp-spec-body]');
    if (!body) return;
    if (!rows.length) { el.hidden = true; return; }
    body.replaceChildren(...rows.map(([label, value]) => {
      const tr = document.createElement('tr');
      const th = document.createElement('th');
      th.scope = 'row';
      th.textContent = label;
      const td = document.createElement('td');
      td.textContent = value;
      tr.append(th, td);
      return tr;
    }));
    el.hidden = false;
  }

  /* shipping nudge */

  renderShipping(v) {
    const el = this.shipEl;
    if (!el || !v) return;
    const threshold = Number(el.dataset.threshold || 0);
    const maxRemaining = Number(el.dataset.maxRemaining || 0);
    const qty = Math.max(1, Number(this.qtyInput && this.qtyInput.value) || 1);
    const remaining = threshold - this.cartTotal - v.price * qty;
    const text = el.querySelector('[data-pdp-ship-text]');
    if (!v.available || threshold <= 0) { el.hidden = true; return; }

    if (remaining <= 0) {
      if (text) text.textContent = el.dataset.reached || '';
      el.classList.add('is-reached');
      el.hidden = !el.dataset.reached;
    } else if (remaining <= maxRemaining) {
      if (text) text.textContent = (el.dataset.unreached || '').replace('{remaining}', this.money(remaining));
      el.classList.remove('is-reached');
      el.hidden = !el.dataset.unreached;
    } else {
      el.hidden = true;
    }
  }

  refreshCart() {
    if (!this.shipEl) return;
    getCart().then((cart) => {
      if (cart && typeof cart.total_price === 'number') {
        this.cartTotal = cart.total_price;
        this.renderShipping(this.lastVariant);
      }
    });
  }

  /* Recharge mount: hide the skeleton once the widget lands, fail safe if it never does */

  watchSubscription() {
    const el = this.root.querySelector('[data-pdp-sub]');
    if (!el) return;
    const parent = el.querySelector('.rc-widget-injection-parent');
    const note = el.querySelector('[data-pdp-sub-note]');
    const mounted = () => {
      el.classList.add('is-mounted');
      if (note) note.hidden = false;
      /* Recharge injects its container first and fills the prices a beat later, so the benefit line is
         attempted a few times and stops as soon as it lands. syncSubscriptionBenefit is idempotent. */
      [0, 250, 750, 1500].forEach((delay) => window.setTimeout(() => this.syncSubscriptionBenefit(), delay));
    };
    if (parent && parent.children.length) { mounted(); return; }

    const observer = new MutationObserver(() => {
      if (parent && parent.children.length) { mounted(); observer.disconnect(); }
    });
    if (parent) observer.observe(parent, { childList: true, subtree: true });

    window.setTimeout(() => {
      if (!el.classList.contains('is-mounted')) { el.classList.add('is-empty'); observer.disconnect(); }
    }, 8000);
  }

  /* Spell out what "Subscribe & save" is worth, in money, from the widget's OWN two prices — no second source
     of truth and nothing invented. Recharge re-renders its prices on variant change, so this runs again then.
     Everything is optional: if the markup or the parse changes, the line simply does not appear. */
  /* Mark the chosen option with a class. Recharge hides its own radio and its selected styling is a faint tint,
     and :has(input:checked) did not re-invalidate when a radio was unchecked by its sibling, so the border and
     tint stayed on the previous option. */
  syncSubscriptionSelection() {
    const el = this.root.querySelector('[data-pdp-sub]');
    if (!el) return;
    const options = [...el.querySelectorAll('.rc-option')];
    const key = options.map((o) => (o.querySelector('input') || {}).checked ? '1' : '0').join('') + ':' + options.length;
    const restyled = options.some((o) => !o.style.borderColor);
    if (key === this.subSelectionKey && !restyled) return;
    this.subSelectionKey = key;

    options.forEach((option) => {
      const input = option.querySelector('input[type="radio"], input');
      /* Recharge sets its own rc-option--active during its render; trust either signal */
      const on = Boolean((input && input.checked) || option.classList.contains('rc-option--active'));
      option.classList.toggle('pdp-sub-option--on', on);
      /* Set the two state colours INLINE rather than leaving them to the class rule. Measured on this widget:
         with the class lists provably correct, the computed border/background stayed on the previously selected
         option -- the engine did not re-match the rule after Recharge re-rendered the subtree. Inline styles
         cannot miss an invalidation, and the class is still there for anything that reads it. */
      const accent = on ? 'rgb(44, 155, 192)' : 'rgba(21, 14, 1, 0.14)';
      option.style.setProperty('border-color', accent, 'important');
      option.style.setProperty('background-color', on ? 'rgba(44, 155, 192, 0.07)' : '#fff', 'important');
    });
    if (!el.dataset.pdpSelectionBound) {
      el.dataset.pdpSelectionBound = '1';
      ['change', 'click'].forEach((name) => {
        el.addEventListener(name, () => window.setTimeout(() => this.syncSubscriptionSelection(), 60));
      });
      /* Choosing "Subscribe & save" makes Recharge re-render the widget (it has to draw the frequency control),
         which replaces the option nodes and takes the styling with them. The events above fire before that, so
         without this the card kept the old highlight until the shopper touched it again. childList only: setting
         inline styles changes attributes, so this cannot re-trigger itself. */
      new MutationObserver(() => {
        window.clearTimeout(this.subSelectionTimer);
        this.subSelectionTimer = window.setTimeout(() => this.syncSubscriptionSelection(), 40);
      }).observe(el, { childList: true, subtree: true });

    }
  }

  syncSubscriptionBenefit() {
    this.syncSubscriptionSelection();
    const el = this.root.querySelector('[data-pdp-sub]');
    if (!el) return;
    /* Recharge mixes naming styles: the element carries rc-option__price AND rc_widget__price, but the
       --onetime / --subsave modifier only exists on the UNDERSCORE form. */
    const once = el.querySelector('.rc_widget__price--onetime, .rc-option__price--onetime');
    const sub = el.querySelector('.rc_widget__price--subsave, .rc-option__price--subsave');
    if (!once || !sub) return;

    const toCents = (node) => {
      const digits = (node.textContent || '').replace(/[^\d.,]/g, '').replace(/,/g, '');
      const value = parseFloat(digits);
      return Number.isFinite(value) ? Math.round(value * 100) : null;
    };
    const full = toCents(once);
    const discounted = toCents(sub);
    if (full === null || discounted === null || discounted >= full) return;

    /* The line lives OUTSIDE the widget, as a sibling of its mount. Appending inside Recharge's own label meant
       the widget rebuilt that node on every render and orphaned it beside the price instead of under it. */
    const mount = el.querySelector('.rc-widget-injection-parent') || el;
    let line = el.querySelector('.pdp-sub__saving');
    if (!line) {
      line = document.createElement('p');
      line.className = 'pdp-sub__saving';
      mount.after(line);
    }
    const text = 'Save ' + this.money(full - discounted) + ' every delivery';
    if (line.textContent !== text) line.textContent = text;

    if (!el.querySelector('.pdp-sub__perks')) {
      const perks = document.createElement('ul');
      perks.className = 'pdp-sub__perks';
      const tick = '<svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8.5l3 3 7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      perks.innerHTML = ['Skip or cancel anytime', 'Never run out'].map((t) => '<li>' + tick + '<span>' + t + '</span></li>').join('');
      const note = el.querySelector('[data-pdp-sub-note]');
      if (note) note.before(perks);
      else el.appendChild(perks);
    }
  }

  /* The sticky bar's variant line.

     <product-rerender observe-form> is NOT a registered custom element in this theme (7.0.1) — it is inert
     markup carried in from a newer Impact, so nothing inside it is refetched when the variant changes. The
     bar's photo and price still follow the picker because <variant-media> and <sale-price> are real elements
     that watch the form themselves. A Liquid string is not, which is how the bar came to read
     "Gold · Single 4oz Can" while Vetiver was selected: right price, right photo, wrong words, on the one
     control that adds to the cart.

     Written from the same variant object the rest of the column renders from, so it cannot disagree with the
     tiles or the price. Products with a single default variant never render the line, so this is a no-op there. */

  renderStickyBar(variant) {
    const line = document.querySelector('.pdp-quick-add .pdp-quick-add__variant-name');
    if (!line || !variant || !variant.title) return;
    line.textContent = variant.title.split(' / ').join(' · ');
  }

  /* the Added moment on every submit button bound to this form, sticky bar included */

  addedMoment() {
    const buttons = new Set([
      ...(this.form ? this.form.querySelectorAll('button[type="submit"]') : []),
      ...document.querySelectorAll('button[type="submit"][form="' + this.formId + '"]')
    ]);
    buttons.forEach((button) => {
      const target = button.getAttribute('is') === 'custom-button' ? button.firstElementChild : button;
      if (!target || button.dataset.pdpAdded) return;
      button.dataset.pdpAdded = '1';
      const original = target.innerHTML;
      target.innerHTML = '<span class="pdp-atc__added">Added <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true" style="vertical-align:-2px"><path d="M3 8.5l3 3 7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';
      this.replay(button, 'is-added');
      window.setTimeout(() => {
        if (button.dataset.pdpAdded === '1' && target.querySelector('.pdp-atc__added')) target.innerHTML = original;
        delete button.dataset.pdpAdded;
        button.classList.remove('is-added');
      }, 1500);
    });
  }

  replay(el, className) {
    el.classList.remove(className);
    void el.offsetWidth; /* restart the CSS animation */
    el.classList.add(className);
  }
}

/* =====================================================================================
   Cross-sell: scent-matched family items, per-item add, atomic "Add all", dedupe on cart:change
   ===================================================================================== */

class PdpCrossSell {
  constructor(root) {
    this.root = root;
    this.formId = root.dataset.formId;
    this.form = document.getElementById(this.formId);
    this.column = document.querySelector('[data-pdp][data-form-id="' + this.formId + '"]');
    this.moneyFormat = (this.column && this.column.dataset.moneyFormat) || '${{amount}}';
    this.scentPosition = Number(root.dataset.scentPosition || 0);
    this.items = Array.from(root.querySelectorAll('[data-xsell-item]')).map((el) => this.parseItem(el));
    this.addAll = root.querySelector('[data-xsell-add-all]');
    this.error = root.querySelector('[data-xsell-error]');
    this.busy = false;

    this.bind();
    this.resolve();
    getCart().then((cart) => { if (cart && cart.items) this.applyCart(cart); });
  }

  parseItem(el) {
    let variants = null;
    if (el.dataset.variants) {
      try { variants = JSON.parse(el.dataset.variants); } catch (e) { variants = null; }
    }
    return {
      el,
      mode: el.dataset.cardMode,
      productId: Number(el.dataset.productId),
      variants,
      check: el.querySelector('[data-xsell-check]'),
      add: el.querySelector('[data-xsell-add]')
    };
  }

  money(cents) {
    return formatMoney(cents, this.moneyFormat);
  }

  bind() {
    if (this.form) this.form.addEventListener('variant:change', () => this.resolve());
    this.items.forEach((item) => {
      if (item.check) item.check.addEventListener('change', () => this.updateTotals());
      if (item.add) item.add.addEventListener('click', () => this.addItems([item], item.add));
    });
    if (this.addAll) this.addAll.addEventListener('click', () => this.addItems(this.selected(), this.addAll));
    document.addEventListener('cart:change', (event) => {
      const cart = event.detail && event.detail.cart;
      if (cart && cart.items) this.applyCart(cart);
    });
  }

  selectedScent() {
    if (!this.scentPosition || !this.column) return null;
    const fieldset = this.column.querySelector('[data-pdp-option][data-option-position="' + this.scentPosition + '"]');
    const checked = fieldset && fieldset.querySelector('input[type="radio"]:checked');
    return checked ? checked.value : null;
  }

  /* pick, per scent card, the cheapest available variant in the selected scent (or the cheapest available at all) */
  resolve() {
    const scent = this.selectedScent();
    const heading = this.root.querySelector('[data-xsell-heading-scent]');
    if (heading && scent) heading.textContent = scent;

    this.items.forEach((item) => {
      if (item.mode !== 'scent' || !item.variants) return;
      const pool = item.variants.filter((v) => v.available).sort((a, b) => a.price - b.price);
      const exact = pool.find((v) => v.scent === scent);
      const pick = exact || pool[0];
      if (!pick) { item.el.dataset.available = 'false'; return; }

      const changed = String(pick.id) !== item.el.dataset.variantId;
      item.el.dataset.variantId = pick.id;
      item.el.dataset.price = pick.price;
      item.el.dataset.available = 'true';
      item.el.classList.toggle('is-substitute', !exact);

      const img = item.el.querySelector('[data-xsell-img]');
      if (img && pick.image && changed) { img.removeAttribute('srcset'); img.src = pick.image; }
      const scentEl = item.el.querySelector('[data-xsell-scent]');
      if (scentEl) scentEl.textContent = pick.scent;
      const price = item.el.querySelector('[data-xsell-price]');
      if (price) price.textContent = this.money(pick.price);
      const onSale = Number(pick.compare) > Number(pick.price);
      const compare = item.el.querySelector('[data-xsell-compare]');
      if (compare) { compare.hidden = !onSale; if (onSale) compare.textContent = this.money(pick.compare); }
      const save = item.el.querySelector('[data-xsell-save]');
      if (save) { save.hidden = !onSale; if (onSale) save.textContent = 'Save ' + Math.round((pick.compare - pick.price) / pick.compare * 100) + '%'; }
      const link = item.el.querySelector('[data-xsell-link]');
      if (link) link.href = item.el.dataset.url + '?variant=' + pick.id;
      if (changed) replay(item.el, 'is-swapping');
    });

    this.updateTotals();
  }

  visible(item) {
    return !item.el.hidden && item.el.dataset.available !== 'false' && item.mode !== 'quick';
  }

  selected() {
    return this.items.filter((item) => this.visible(item) && item.check && item.check.checked);
  }

  updateTotals() {
    if (!this.addAll) return;
    const chosen = this.selected();
    const total = chosen.reduce((sum, item) => sum + Number(item.el.dataset.price || 0), 0);
    const totalEl = this.addAll.querySelector('[data-xsell-all-total]');
    if (totalEl) totalEl.textContent = chosen.length ? this.money(total) : '';
    this.addAll.classList.toggle('is-empty', chosen.length === 0);
    this.addAll.disabled = chosen.length === 0 || this.busy;
  }

  /* anything already in the cart is not offered again; an empty list hides the block */
  applyCart(cart) {
    const inCart = new Set((cart.items || []).map((line) => line.product_id));
    let shown = 0;
    this.items.forEach((item) => {
      const hide = inCart.has(item.productId);
      item.el.hidden = hide;
      if (!hide) shown += 1;
    });
    this.root.hidden = shown === 0;
    this.updateTotals();
  }

  async addItems(items, button) {
    const lines = items.filter((item) => this.visible(item)).map((item) => ({ id: Number(item.el.dataset.variantId), quantity: 1 }));
    if (!lines.length || this.busy) return;
    this.busy = true;
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
    if (this.error) this.error.hidden = true;

    /* same bundled-sections handshake ProductForm uses, so the drawer gets its HTML in the same response */
    const sections = ['variant-added'];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));

    let response;
    let json = {};
    try {
      response = await fetch(routeRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: lines, sections: sections.join(','), sections_url: routeRoot() + 'variants/' + lines[0].id })
      });
      json = await response.json();
    } catch (e) {
      this.fail('Could not reach the cart. Check your connection and try again.');
      this.done(button);
      return;
    }

    if (!response.ok) {
      this.fail((json && (json.description || json.message)) || 'Could not add to cart. Please try again.');
      this.done(button);
      return;
    }

    const cart = await getCart(true);
    if (cart) cart.sections = json.sections;
    const added = json.items || [json];
    this.root.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: added, cart } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));

    items.forEach((item) => item.el.classList.add('is-added'));
    if (button) {
      const label = button.querySelector('[data-xsell-all-label]') || button;
      const original = label.innerHTML;
      label.innerHTML = ADDED_HTML;
      replay(button, 'is-added');
      window.setTimeout(() => { label.innerHTML = original; button.classList.remove('is-added'); }, 1500);
    }
    this.done(button);
  }

  fail(message) {
    if (this.error) { this.error.textContent = message; this.error.hidden = false; }
  }

  done(button) {
    this.busy = false;
    if (button) { button.disabled = false; button.removeAttribute('aria-busy'); }
    this.updateTotals();
  }
}

/* =====================================================================================
   Product family row, the "Type" tiles of snippets/pdp-family.liquid  (YourBrandFuel 2026-09-29)

   The tiles are plain links to sibling products, so the row works with no script at all. This only keeps the
   scent carrying across after the shopper changes it here: on every variant change (and once at boot, for a pick
   made before this script ran) each tile is re-pointed at its product's pick for the new scent, read from the
   per-scent map Liquid wrote on the tile (cheapest available in that scent, else cheapest in that scent, else the
   product's default). This page's own tile follows the selected variant and never navigates.
   ===================================================================================== */

class PdpFamily {
  constructor(root) {
    const column = root.closest('.pdp-buy[data-form-id]');
    const parse = (value) => { try { return JSON.parse(value); } catch (e) { return null; } };
    this.form = column ? document.getElementById(column.dataset.formId) : null;
    this.moneyFormat = (column && column.dataset.moneyFormat) || '${{amount}}';
    this.scentIndex = Number(root.dataset.scentIndex);
    this.byId = new Map((parseJson(column && column.querySelector('[data-pdp-variant-map]')) || []).map((v) => [String(v.id), v]));
    this.tiles = Array.from(root.querySelectorAll('[data-family-tile]')).map((el) => ({
      el,
      url: el.dataset.url,
      current: el.hasAttribute('data-current'),
      picks: new Map(Object.entries(parse(el.dataset.familyMap) || {})),
      fallback: parse(el.dataset.familyDefault),
      price: el.querySelector('[data-family-price]'),
      note: el.querySelector('[data-family-note]')
    }));

    this.tiles.forEach((tile) => {
      if (tile.current) tile.el.addEventListener('click', (event) => event.preventDefault());
    });
    if (this.form) this.form.addEventListener('variant:change', (event) => this.render(event.detail && event.detail.variant));

    /* this module can arrive after the theme's picker is live (slow connections), and a pick made in that window
       fired its variant:change unheard: catch up with the form once, as PdpBuyColumn does. A no-op otherwise. */
    const idField = this.form && this.form.elements ? this.form.elements.id : null;
    if (idField) this.render(this.byId.get(String(idField.value)));
  }

  render(variant) {
    if (!variant || !variant.id) return;
    const v = this.byId.get(String(variant.id)) || variant;
    const scent = this.scentIndex >= 0 && Array.isArray(v.options) ? v.options[this.scentIndex] : null;
    this.tiles.forEach((tile) => {
      /* this page's own tile falls back to the selected variant, as its first paint does (selected_or_first_available) */
      const pick = tile.picks.get(scent) || (tile.current ? v : tile.fallback);
      if (tile.current) tile.el.href = tile.url + '?variant=' + v.id;
      if (!pick) return;
      if (!tile.current) tile.el.href = tile.url + '?variant=' + pick.id;
      if (tile.price) tile.price.textContent = formatMoney(pick.price, this.moneyFormat);
      if (tile.note) tile.note.hidden = Boolean(pick.available);
    });
  }
}

/* =====================================================================================
   Desktop gallery: hover a thumbnail to show it, and a loupe over the main image.
   Same treatment as velvetsylph.com's pdp-gallery.js (190px circle, 2.2x, cursor-centred).

   Theme facts this relies on (assets/theme.js.liquid):
     - <media-carousel> exposes select(index, {animate}) and selectedSlide / selectedIndex;
       thumbnails and slides share the same absolute index (media position - 1), which is why
       curated-out nodes stay in the DOM.
     - PageDots with align-selected re-centres the rail on every select(); under a hovering
       cursor that would slide the thumbnails away, so the attribute is lifted while hovering.
     - Anything positioned inside <media-carousel> is clipped by adaptive-height and pages the
       gallery on click, so the loupe sits on .product-gallery__media-list-wrapper instead.
   ===================================================================================== */

const LOUPE_D = 190;
const LOUPE_Z = 2.2;

class PdpGalleryDesktop {
  constructor(gallery) {
    this.gallery = gallery;
    this.wrapper = gallery.querySelector('.product-gallery__media-list-wrapper');
    this.carousel = gallery.querySelector('media-carousel');
    this.rail = gallery.querySelector('.product-gallery__thumbnail-list');
    if (!this.wrapper || !this.carousel) return;
    this.fine = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.desktop = window.matchMedia('(min-width: 1000px)');
    this.lastTouch = 0;
    this.loupe = null;
    this.bindHover();
    this.bindLoupe();
    this.bindOpen();
    this.bindVariantFilter();
    this.bindResize();
  }

  /* Dragging the window wider slid the gallery sideways between slides. The theme's ScrollCarousel only observes
     HEIGHT (its ResizeObserver calls _adjustHeight), so on a width change the scroll offset stays in old pixels
     while the slide width changes underneath it, and the carousel comes to rest showing halves of two images.
     Re-selecting the current index snaps it back; force is needed because the index itself has not changed.
     rAF-throttled so it tracks during the drag rather than settling afterwards. */
  bindResize() {
    let realigning = false;
    const realign = () => {
      if (realigning || !this.carousel || typeof this.carousel.select !== 'function') return;
      const index = this.carousel.selectedIndex;
      if (typeof index !== 'number' || index < 0) return;
      realigning = true;
      try {
        this.carousel.select(index, { animate: false, force: true });
      } finally {
        realigning = false;
      }
    };
    /* Synchronous, not requestAnimationFrame: the browser already coalesces resize to about one event per frame,
       and a frame-scheduled realign never runs in a backgrounded or non-painting tab, which is where this gets
       tested. A trailing timeout catches the final size once the drag stops. */
    let trailing = 0;
    const onResize = () => {
      this.hideLoupe();
      realign();
      window.clearTimeout(trailing);
      trailing = window.setTimeout(realign, 120);
    };
    window.addEventListener('resize', onResize);

    /* Also watch the carousel itself, so a width change that does not come from a window resize (a container
       query, browser zoom, the theme editor pane) is corrected too. Width only: the theme already observes
       height, and realigning scrolls rather than resizes, so this cannot feed itself. */
    if (window.ResizeObserver) {
      let lastWidth = Math.round(this.carousel.getBoundingClientRect().width);
      new ResizeObserver(() => {
        const width = Math.round(this.carousel.getBoundingClientRect().width);
        if (width === lastWidth) return;
        lastWidth = width;
        onResize();
      }).observe(this.carousel);
    }
  }

  /* Per-variant image sets. product-gallery.liquid renders the right set for the first paint; this keeps it right
     as the shopper changes variant, using the theme's own filter events rather than touching the DOM directly.

     The theme's ProductGallery._onVariantChanged runs first and dispatches its own (empty, because this store has
     no alt-tag image sets) filter, which unhides everything, then selects the variant's featured media. So this
     re-applies on a timeout, i.e. after that synchronous handler, and never fights it. */
  bindVariantFilter() {
    if (!this.gallery.hasAttribute('data-variant-filter')) return;
    const column = document.querySelector('[data-pdp]');
    const map = column && column.querySelector('[data-pdp-variant-map]');
    this.variants = parseJson(map);
    if (!Array.isArray(this.variants) || !this.variants.length) return;

    /* the swatch option IS the group option: both come from the block's swatch_options setting */
    const swatch = column.querySelector('.pdp-option--swatch[data-option-position]');
    this.groupIndex = swatch ? Number(swatch.dataset.optionPosition) - 1 : -1;
    /* size-specific products (section setting): a claimed photo follows the exact variant, not the scent.
       Scent tags still use groupValue, so only the claim rule changes. Mirrors product-gallery.liquid. */
    this.exactVariant = this.gallery.hasAttribute('data-variant-filter-exact');

    const form = document.forms[this.gallery.getAttribute('form')];
    if (form) {
      form.addEventListener('variant:change', (event) => {
        window.setTimeout(() => this.filterMedia(event.detail.variant && event.detail.variant.id), 0);
      });
    }
    window.setTimeout(() => this.filterMedia(), 0);
  }

  filterMedia(variantId) {
    if (!Array.isArray(this.variants)) return;
    const items = this.carousel.items || Array.from(this.carousel.children);
    if (!items.length) return;

    const selected = this.variants.find((v) => String(v.id) === String(variantId)) ||
      this.variants.find((v) => String(v.id) === String(this.selectedVariantId())) ||
      this.variants[0];
    if (!selected) return;

    const gi = this.groupIndex;
    const groupValue = gi >= 0 && Array.isArray(selected.options) ? selected.options[gi] : null;

    const claimed = new Set();
    const allowed = new Set();
    this.variants.forEach((v) => {
      const pos = Number(v.media_position);
      if (!pos) return;
      claimed.add(pos);
      const keep = !this.exactVariant && groupValue !== null && Array.isArray(v.options)
        ? v.options[gi] === groupValue
        : String(v.id) === String(selected.id);
      if (keep) allowed.add(pos);
    });
    /* Alt-tag image sets, Impact's "<description>#Option_Value" convention. product-gallery.liquid
       honours these at first paint and the theme's own _getFilteredMediaIndexes honours them on
       variant:change, but this filter used to key off the variant featured image ALONE. That treats
       "no variant claims it" as a positive assertion of "generic", when it usually just means the
       photo was never wired up, which is how a Gold bottle ended up on the Royal Oud gallery.
       Reading the tags here keeps all three paths agreeing. */
    const groupName = (() => {
      /* inputs are named option1/option2, so the human option name comes off the legend */
      const el = document.querySelector('.pdp-option--swatch .pdp-option__name');
      const t = el && el.textContent ? el.textContent.trim().toLowerCase() : '';
      return t || null;
    })();
    const tagged = new Map();
    items.forEach((item, index) => {
      const img = item.querySelector('img');
      const alt = img && img.getAttribute('alt');
      if (!alt || alt.indexOf('#') === -1) return;
      const parts = alt.split('#').pop().split('_');
      if (parts.length < 2) return;
      if (groupName && parts[0].trim().toLowerCase() !== groupName) return;
      tagged.set(index + 1, parts.slice(1).join('_').trim().toLowerCase());
    });
    const groupValueLc = groupValue === null || groupValue === undefined ? null : String(groupValue).toLowerCase();
    /* scent-specific means claimed by a variant OR tagged; anything else is a true generic */
    const isSpecific = (pos) => claimed.has(pos) || tagged.has(pos);
    /* In size-specific mode a variant's own photo is often ALSO scent-tagged (the Royal Oud 3-pack carries
       #Scent_Royal Oud), so the claim decides for claimed photos and the tag only for unclaimed graphics.
       Otherwise Royal Oud Single would show the Royal Oud 3-pack shot after the first variant change. */
    const isAllowed = (pos) => ((this.exactVariant && claimed.has(pos)) ? allowed.has(pos) : (allowed.has(pos) ||
      (tagged.has(pos) && groupValueLc !== null && tagged.get(pos) === groupValueLc)));

    /* A scent that claims nothing used to bail out here, which shows EVERY other scent's photos.
       "This variant has no featured image" is missing information, not an instruction to show
       everything. Only bail when the product has no scent-specific media at all, in which case
       there is genuinely nothing to filter. Otherwise fall through: the scent gets the generics,
       which is the honest answer. */
    const anySpecific = claimed.size > 0 || tagged.size > 0;
    if (!anySpecific) return;

    /* Same floor as the Liquid, and the same reasoning. It used to be 3, and falling below it skipped
       the filter entirely, which shows every other scent's photos. That is always the wrong answer and
       it fails silently. The floor is now 1: it means "never render an empty gallery", not "give up".
       Curated-out media never count, they are already gone. */
    const minimum = Number(this.gallery.dataset.variantFilterMinimum || 1);
    let wouldRemain = 0;
    items.forEach((item, index) => {
      if (item.classList.contains('pdp-media--out')) return;
      const pos = index + 1;
      if (!isSpecific(pos) || isAllowed(pos)) wouldRemain += 1;
    });

    const filtered = [];
    if (wouldRemain >= minimum) {
      for (let pos = 1; pos <= items.length; pos += 1) {
        if (isSpecific(pos) && !isAllowed(pos)) filtered.push(pos - 1);
      }
    }

    this.carousel.dispatchEvent(new CustomEvent('carousel:filter', { detail: { filteredIndexes: filtered } }));
    this.gallery.querySelectorAll('page-dots').forEach((dots) => {
      dots.dispatchEvent(new CustomEvent('control:filter', { detail: { filteredIndexes: filtered } }));
    });

    /* The theme's own handler unhides everything and scrolls the carousel BEFORE this runs on the
       next task, so without this the carousel can be sitting on a slide that has just been hidden,
       or scrolled to a position computed against the unfiltered layout. Re-assert the selection
       against the filtered set: prefer the variant's own featured media, else the first slide that
       survived. Guarded because select() is the theme's public API and may be absent on old builds. */
    const hidden = new Set(filtered);
    let target = Number(selected.media_position) ? Number(selected.media_position) - 1 : -1;
    if (target < 0 || hidden.has(target)) {
      target = -1;
      for (let i = 0; i < items.length; i += 1) {
        if (hidden.has(i) || items[i].classList.contains('pdp-media--out')) continue;
        target = i; break;
      }
    }
    if (target >= 0 && typeof this.carousel.select === 'function') {
      /* force: the theme's handler has usually just selected this SAME index, but against its own (alt-tag only)
         filter, where the scent's other size photos were still in front of it. This filter then hides them, so
         the index is unchanged while the slide's offset is not. Without force, select() is a no-op and the
         result depends on the browser re-snapping; with it the offset is recomputed from the filtered layout,
         so 3-Pack and 6 Pack always rest on their own photo. Same reason as bindResize(). */
      try { this.carousel.select(target, { animate: false, force: true }); } catch (e) { /* older carousel build */ }
    }
  }

  selectedVariantId() {
    const form = document.forms[this.gallery.getAttribute('form')];
    const field = form && (form.elements.id || form.querySelector('[name="id"]'));
    return field ? field.value : null;
  }

  active() {
    return this.fine.matches && this.desktop.matches;
  }

  bindHover() {
    if (!this.rail) return;
    /* Browsers fire pointerover BEFORE pointerenter, so the attribute is lifted inside the same
       handler that selects, ahead of the select() call; pointerleave restores it. Otherwise
       PageDots re-centres an overflowing rail under the stationary cursor and the neighbour
       that lands there gets selected instead. */
    this.rail.addEventListener('pointerleave', () => {
      if (!this.rail.dataset.pdpAlign) return;
      this.rail.setAttribute('align-selected', '');
      delete this.rail.dataset.pdpAlign;
    });
    this.rail.addEventListener('pointerover', (event) => {
      if (!this.active() || event.pointerType !== 'mouse') return;
      if (this.rail.hasAttribute('align-selected')) {
        this.rail.dataset.pdpAlign = '1';
        this.rail.removeAttribute('align-selected');
      }
      const thumb = event.target.closest('.product-gallery__thumbnail');
      if (!thumb || thumb.hidden || thumb.classList.contains('pdp-media--out')) return;
      const index = Array.prototype.indexOf.call(this.rail.children, thumb);
      if (index < 0 || index === this.carousel.selectedIndex) return;
      this.hideLoupe();
      if (typeof this.carousel.select === 'function') this.carousel.select(index, { animate: false });
      else thumb.click();
    });
  }

  bindLoupe() {
    if (!this.fine.matches) return;
    this.loupe = document.createElement('div');
    this.loupe.className = 'pdp-loupe';
    this.loupe.setAttribute('aria-hidden', 'true');
    this.wrapper.appendChild(this.loupe);
    this.wrapper.classList.add('pdp-has-loupe');

    this.carousel.addEventListener('touchstart', () => { this.lastTouch = Date.now(); }, { passive: true });
    this.carousel.addEventListener('mousemove', (event) => this.moveLoupe(event));
    this.carousel.addEventListener('mouseleave', () => this.hideLoupe());
    this.carousel.addEventListener('scroll', () => this.hideLoupe(), { passive: true });
    document.addEventListener('variant:change', () => this.hideLoupe());
    window.addEventListener('resize', () => this.hideLoupe());
    this.wrapper.querySelectorAll('[is="product-zoom-button"]').forEach((button) => button.addEventListener('click', () => this.hideLoupe()));
  }

  /* The photo itself opens the theme's lightbox, at every width, which is why the corner magnifier
     button is hidden (pdp-v2.css) — it stays in the DOM because this is what it clicks, and keyboard
     users can still tab to it. On desktop this also replaces MediaCarousel._onGalleryClick, which
     otherwise pages the gallery on a fine-pointer click; capture phase runs before that listener.
     On touch the browser suppresses the click when the gesture turned into a swipe, so a tap opens
     the lightbox and a swipe still scrolls the carousel. */
  bindOpen() {
    const carousel = this.carousel || this.gallery.querySelector('media-carousel');
    if (!carousel) return;
    carousel.addEventListener('click', (event) => {
      if (event.target.closest('button, a[href]')) return;
      const img = this.currentImage();
      if (!img || !img.contains(event.target)) return;
      const zoom = this.wrapper.querySelector('[is="product-zoom-button"]');
      if (!zoom) return;
      event.stopPropagation();
      event.preventDefault();
      this.hideLoupe();
      zoom.click();
    }, true);
  }

  currentImage() {
    const slide = this.carousel.selectedSlide || this.carousel.querySelector('.product-gallery__media.is-selected');
    if (!slide || slide.getAttribute('data-media-type') !== 'image') return null;
    return slide.querySelector('img');
  }

  /* the largest srcset candidate that covers the magnified size on this screen, capped at 2400 */
  hiRes(img) {
    const want = Math.min(2400, Math.round(img.getBoundingClientRect().width * LOUPE_Z * (window.devicePixelRatio || 1)));
    const candidates = (img.srcset || '').split(',')
      .map((entry) => entry.trim().split(/\s+/))
      .map(([url, width]) => ({ url, w: parseInt(width, 10) || 0 }))
      .filter((candidate) => candidate.url)
      .sort((a, b) => a.w - b.w);
    if (!candidates.length) return img.currentSrc || img.src;
    return (candidates.find((candidate) => candidate.w >= want) || candidates[candidates.length - 1]).url;
  }

  moveLoupe(event) {
    if (!this.active() || Date.now() - this.lastTouch < 700) { this.hideLoupe(); return; }
    const img = this.currentImage();
    if (!img || !img.naturalWidth) { this.hideLoupe(); return; }
    const rect = img.getBoundingClientRect();
    const ix = event.clientX - rect.left;
    const iy = event.clientY - rect.top;
    if (ix < 0 || iy < 0 || ix > rect.width || iy > rect.height) { this.hideLoupe(); return; }

    /* two layers: the hi-res candidate on top, the already-decoded on-screen image underneath,
       so the disc is never blank while the larger file downloads */
    const src = this.hiRes(img);
    const shown = img.currentSrc || img.src;
    const key = src + '|' + shown;
    if (this.loupe.dataset.src !== key) {
      this.loupe.style.backgroundImage = 'url("' + src + '")' + (shown && shown !== src ? ', url("' + shown + '")' : '');
      this.loupe.dataset.src = key;
    }
    const wrap = this.wrapper.getBoundingClientRect();
    const size = (rect.width * LOUPE_Z) + 'px ' + (rect.height * LOUPE_Z) + 'px';
    const pos = (-(ix * LOUPE_Z - LOUPE_D / 2)) + 'px ' + (-(iy * LOUPE_Z - LOUPE_D / 2)) + 'px';
    this.loupe.style.left = (event.clientX - wrap.left - LOUPE_D / 2) + 'px';
    this.loupe.style.top = (event.clientY - wrap.top - LOUPE_D / 2) + 'px';
    this.loupe.style.backgroundSize = size + ', ' + size;
    this.loupe.style.backgroundPosition = pos + ', ' + pos;
    this.loupe.classList.add('is-on');
  }

  hideLoupe() {
    if (this.loupe) this.loupe.classList.remove('is-on');
  }
}

document.querySelectorAll('.pdp product-gallery').forEach((gallery) => {
  if (!gallery.dataset.pdpGalleryReady) {
    gallery.dataset.pdpGalleryReady = '1';
    new PdpGalleryDesktop(gallery);
  }
});

/* =====================================================================================
   Review wall loading

   Okendo renders 5 reviews and its "Show more" adds 5 at a time, which is far too slow for a six-column wall.
   The owner's spec: load as you scroll up to 100, then hand over to a Show more that adds 25 per click. Phones
   get a smaller wall, 60 then 15, because 100 cards in a 3-column phone layout is a very long scroll and every
   batch costs a request.

   Two Okendo details this depends on:
     - the control IS the .okeReviews-reviews-showMore element (there is no inner <button>), and
     - it is removed (or hidden) from the DOM once every review is loaded, which is the natural stop condition.

   While the wall is auto-loading the control is hidden, because pressing it would only add its own 5 and read as
   broken next to the scroll loading. It is revealed the moment auto-loading ends: at 100, or early if a press
   returns nothing, so a stalled fetch can never leave the shopper with no way to load more.
   ===================================================================================== */
/* Read at call time rather than at load, so rotating a phone picks up the other set */
const isPhoneWall = () => window.matchMedia('(max-width: 699px)').matches;
const reviewScrollLimit = () => (isPhoneWall() ? 60 : 100);
const reviewClickBatch = () => (isPhoneWall() ? 15 : 25);

const reviewCount = () => document.querySelectorAll('.okeReviews-reviews-review').length;
const showMoreEl = () => document.querySelector('.okeReviews-reviews-showMore');

let reviewWallBusy = false;

/* Press Okendo's control until `target` is reached. Resolves with { count, exhausted } so the caller can tell
   "we hit the target" from "there is nothing left to load". */
async function pressShowMore(target) {
  let exhausted = false;
  while (reviewCount() < target) {
    const control = showMoreEl();
    if (!control || control.offsetParent === null) { exhausted = true; break; }
    const before = reviewCount();
    control.click();
    let waited = 0;
    while (waited < 6000 && reviewCount() === before) {
      await new Promise((resolve) => window.setTimeout(resolve, 200));
      waited += 200;
    }
    if (reviewCount() === before) { exhausted = true; break; } /* failed or finished */
  }
  upgradeReviewPhotos();
  return { count: reviewCount(), exhausted };
}

function revealShowMore(widget) {
  widget.classList.add('pdp-reviews--manual');
}

function watchReviewWall() {
  const widget = document.querySelector('.okeReviews-reviewsWidget');
  if (!widget || widget.dataset.pdpWallReady) return;
  widget.dataset.pdpWallReady = '1';

  /* --- phase 1: load on scroll, up to the limit for this screen size --- */
  const maybeLoad = async () => {
    if (reviewWallBusy || widget.classList.contains('pdp-reviews--manual')) return;
    const list = document.querySelector('.okeReviews-reviews-main');
    if (!list) return;
    /* trigger once the foot of the wall is within a screen and a half */
    if (list.getBoundingClientRect().bottom > window.innerHeight * 1.5) return;

    reviewWallBusy = true;
    let result = { count: reviewCount(), exhausted: true };
    try {
      const next = Math.min(reviewScrollLimit(), reviewCount() + reviewClickBatch());
      result = await pressShowMore(next);
    } finally {
      /* finally, not a plain reset: a thrown fetch would otherwise leave the wall permanently "busy" and
         silently stop both the scroll loading and the Show more top-up */
      reviewWallBusy = false;
    }
    if (result.exhausted || result.count >= reviewScrollLimit()) revealShowMore(widget);
  };

  window.addEventListener('scroll', maybeLoad, { passive: true });
  /* a poll as well as the scroll listener: it is what makes this exercisable in a non-painting tab, and it
     covers a wall that is already in view without the shopper having to move */
  const poll = window.setInterval(() => {
    if (widget.classList.contains('pdp-reviews--manual')) { window.clearInterval(poll); return; }
    maybeLoad();
  }, 700);

  /* --- phase 2: each press of Show more adds a full batch, not Okendo's 5 --- */
  document.addEventListener('click', async (event) => {
    const control = event.target.closest('.okeReviews-reviews-showMore');
    if (!control || reviewWallBusy) return;
    /* Okendo's own handler runs on this same click and adds its 5, so this tops the batch up to 25 */
    reviewWallBusy = true;
    try {
      const target = reviewCount() + reviewClickBatch();
      await new Promise((resolve) => window.setTimeout(resolve, 400));
      await pressShowMore(target);
    } finally {
      reviewWallBusy = false;
    }
  });
}

/* exposed so the wall can be driven directly when testing */
window.pdpReviewWall = {
  press: pressShowMore,
  count: reviewCount,
  limit: reviewScrollLimit,
  batch: reviewClickBatch,
  get busy() { return reviewWallBusy; },
  reset() { reviewWallBusy = false; }
};

/* Review photos are served as 120px thumbnails, which is soft when one becomes a full-width hero. Okendo puts
   the real URL in data-oke-reviews-lazy-image-url and swaps it in on view, so both forms are upgraded. */
function upgradeReviewPhotos() {
  const bigger = (url) => (url || '').replace(/([?&]d=)\d+x\d+/, '$1480x480');
  document.querySelectorAll('.okeReviews-review-media img').forEach((img) => {
    const lazy = img.getAttribute('data-oke-reviews-lazy-image-url');
    if (lazy && /d=\d+x\d+/.test(lazy)) img.setAttribute('data-oke-reviews-lazy-image-url', bigger(lazy));
    const src = img.getAttribute('src') || '';
    if (/d=\d+x\d+/.test(src)) img.setAttribute('src', bigger(src));
  });
}

/* "Read more" lifts the CSS clamp on that card. Delegated, so it covers reviews loaded later too. */
document.addEventListener('click', (event) => {
  const readMore = event.target.closest('.okeReviews-review-main-content-readMore, .js-okeReviews-readMore');
  if (!readMore) return;
  const card = readMore.closest('.okeReviews-reviews-review');
  if (card) card.classList.add('pdp-review--open');
});

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchReviewWall);
else watchReviewWall();

/* =====================================================================================
   "You may also like" (AfterSell) rebuilt to match the kit-builder cards

   This widget renders inside an OPEN shadow root, so nothing in pdp-v2.css can reach it — shadow DOM
   encapsulates styles. Styles and markup are injected into the shadow root itself and re-applied on mutation,
   because the widget rebuilds its own DOM on variant change and carousel paging.

   The visible gap was never really colour: the kit builder picks a scent with COLOURED PILLS, this shipped a
   dropdown. So the select is hidden (AfterSell still reads it) and driven by pill rows built from its options.

   Its option lists are not uniform, which is why the rows are built per dimension rather than one pill per
   option: "Complete Beard Care Kit" is 7 plain scents, but "Jelly Beard Oil" is 21 "Scent / Size" combinations
   and "The Professional Beard Kit" is 14 "Scent / Soap" ones. Splitting on " / " gives one row per dimension,
   the second row is filtered to what is actually available for the chosen first value, and selecting resolves
   back to the single matching option.

   Palette and pill shape are lifted from assets/kit-builder.css so the two sections read as one system.
   ===================================================================================== */
const AFTERSELL_SWATCHES = {
  'royal-oud': '#0422b0',
  stagecoach: '#765031',
  magic: '#2bbef1',
  gold: '#dfa709',
  hammer: '#eb1407',
  'lavender-patchouli': '#eb1407',
  vetiver: 'green',
  'eucalyptus-tea-tree': 'green',
  'vetiver-x': '#016423',
  'the-gatsby': '#efb652',
  unscented: '#000',
  naked: '#fff',
  'emerald-green-tea': '#2e9d68',
  grove: '#3e7c46'
};

const swatchKey = (value) => (value || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const AFTERSELL_CSS = `
  .product-card {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px !important;
    background: #fff !important;
    border: 1px solid rgba(21, 14, 1, 0.12) !important;
    border-color: rgba(21, 14, 1, 0.12) !important;
    border-radius: 8px !important;
    transition: border-color .2s ease;
  }

  .product-card:hover { border-color: rgba(44, 155, 192, 0.55) !important; }

  .product-image-wrapper {
    aspect-ratio: 1;
    display: grid !important;
    place-items: center;
    background: #fff;
  }

  .product-image {
    max-width: 100% !important;
    max-height: 100% !important;
    width: 100% !important;
    height: 100% !important;
    object-fit: contain !important;
  }

  .product-title {
    font-size: 14px !important;
    font-weight: 600 !important;
    line-height: 1.35 !important;
    margin: 0 !important;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    min-height: 2.7em;
    color: rgb(21, 14, 1) !important;
  }

  .price-info, .discounted-price-wrapper {
    font-weight: 700 !important;
    color: rgb(21, 14, 1) !important;
  }

  /* the select stays in the DOM because AfterSell reads it; the pills drive it */
  .pdp-pill-host + .select-wrap,
  .select-wrap.pdp-hidden { display: none !important; }

  .pdp-pill-host { display: grid; gap: 8px; }
  .pdp-pill-row { display: grid; gap: 4px; }

  .pdp-pill-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: .06em;
    color: rgba(21, 14, 1, 0.55);
  }

  .pdp-pills { display: flex; flex-wrap: wrap; gap: 5px; }

  .pdp-pill {
    position: relative;
    padding: 5px 9px;
    font-size: 12px;
    line-height: 1.2;
    border: 1px solid rgba(21, 14, 1, 0.25);
    border-radius: 999px;
    background: transparent;
    color: rgb(21, 14, 1);
    cursor: pointer;
    transition: background-color .15s ease, color .15s ease, border-color .15s ease;
  }

  /* expand the touch target to ~44px without changing the visual, as the kit builder does */
  .pdp-pill::after { content: ""; position: absolute; inset: -8px -4px; }

  .pdp-pill[aria-pressed="true"] {
    background: rgb(21, 14, 1);
    border-color: rgb(21, 14, 1);
    color: #fff;
  }

  .pdp-pill[disabled] { opacity: .35; cursor: not-allowed; }

  .pdp-pill__dot {
    display: inline-block;
    width: .625rem; height: .625rem;
    border-radius: 50%;
    margin-right: .375rem;
    vertical-align: -1px;
    background: #d5d5d5;
    border: 1px solid rgba(0, 0, 0, .15);
  }

  .add-button {
    width: 100% !important;
    min-height: 48px !important;
    border: 0 !important;
    border-radius: 8px !important;
    background: rgb(44, 155, 192) !important;
    color: #fff !important;
    font-size: 14px !important;
    font-weight: 700 !important;
    letter-spacing: .04em !important;
    text-transform: uppercase !important;
    cursor: pointer;
    transition: opacity .15s ease;
  }

  .add-button:hover { opacity: .9; }

  /* cards in the row stretch to the tallest, so the buttons line up instead of floating at three heights */
  .add-button { margin-top: auto; }

  .upsell-header {
    font-weight: 700 !important;
    letter-spacing: .01em;
    color: rgb(21, 14, 1) !important;
  }

  /* The app sizes these cards at 164px, which is fine for an image and a price and much too narrow once the
     scent pills are in there: at 164px a seven-scent row is six lines tall and the buttons end up level with
     nothing. One card and a peek of the next, snapped, is the same pattern the gallery uses on a phone. */
  @media screen and (max-width: 699px) {
    .product-card-container.horizontal {
      gap: 12px !important;
      scroll-snap-type: x mandatory;
      scroll-padding: 0;
    }

    .product-card {
      width: min(268px, 82%) !important;
      min-width: min(268px, 82%) !important;
      scroll-snap-align: start;
    }

    /* A square photo on a 268px card is 268px tall and pushes the pills and the button off the screen. The cap
       has to go on the image: the wrapper is a grid whose row is sized by its content, so setting a height on
       the wrapper alone just let the row overflow it and the photo ran over the title. */
    .product-image-wrapper {
      aspect-ratio: auto !important;
      overflow: hidden;
    }

    .product-image {
      height: 190px !important;
    }

    .pdp-pill { font-size: 12.5px; padding: 6px 10px; }
    .pdp-pill-label { font-size: 11.5px; }
  }
`;

function buildAfterSellPills(card) {
  const wrap = card.querySelector('.select-wrap');
  const select = card.querySelector('select');
  if (!wrap || !select || select.options.length < 2) return;
  if (card.querySelector('.pdp-pill-host')) return;

  const options = [...select.options].map((o) => ({ value: o.value, parts: o.text.split('/').map((p) => p.trim()) }));
  const dimensions = Math.max(...options.map((o) => o.parts.length));
  if (dimensions > 2) return; /* beyond two dimensions a dropdown is the honest control */

  /* The wrap holds a <label> AND the <select>, so its textContent is the heading followed by every option glued
     together: "Choose Your Scent / Choose Your SoapStagecoach / Lavender + Patchouli...". Splitting that on "/"
     gave the second row a heading of "Choose Your SoapStagecoach". Read the label element on its own, and if
     there is none, take the wrap with the selects stripped out. */
  const labelSource = wrap.querySelector('label') || (() => {
    const copy = wrap.cloneNode(true);
    copy.querySelectorAll('select').forEach((el) => el.remove());
    return copy;
  })();
  const labels = (labelSource.textContent || '').split('/').map((p) => p.trim()).filter(Boolean);
  const chosen = options.find((o) => o.value === select.value) || options[0];
  const picked = chosen.parts.slice();

  const host = document.createElement('div');
  host.className = 'pdp-pill-host';

  const resolve = () => {
    const match = options.find((o) => o.parts.every((p, i) => p === picked[i])) ||
      options.find((o) => o.parts[0] === picked[0]);
    if (!match) return;
    picked.length = 0;
    match.parts.forEach((p) => picked.push(p));
    select.value = match.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    render();
  };

  const render = () => {
    host.textContent = '';
    for (let dim = 0; dim < dimensions; dim += 1) {
      /* the second row only offers what actually exists for the value chosen in the first */
      const available = options.filter((o) => (dim === 0 ? true : o.parts[0] === picked[0]));
      const values = [...new Set(available.map((o) => o.parts[dim]).filter(Boolean))];
      if (values.length < 2 && dim > 0) continue;

      const row = document.createElement('div');
      row.className = 'pdp-pill-row';
      if (labels[dim]) {
        const label = document.createElement('span');
        label.className = 'pdp-pill-label';
        label.textContent = labels[dim];
        row.appendChild(label);
      }
      const list = document.createElement('div');
      list.className = 'pdp-pills';

      values.forEach((value) => {
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'pdp-pill';
        pill.setAttribute('aria-pressed', String(picked[dim] === value));
        const key = swatchKey(value);
        if (AFTERSELL_SWATCHES[key]) {
          const dot = document.createElement('span');
          dot.className = 'pdp-pill__dot';
          dot.style.background = AFTERSELL_SWATCHES[key];
          pill.appendChild(dot);
        }
        pill.appendChild(document.createTextNode(value));
        pill.addEventListener('click', () => { picked[dim] = value; resolve(); });
        list.appendChild(pill);
      });

      row.appendChild(list);
      host.appendChild(row);
    }
  };

  render();
  wrap.classList.add('pdp-hidden');
  wrap.parentNode.insertBefore(host, wrap);
}

function styleAfterSellWidgets() {
  document.querySelectorAll('*').forEach((host) => {
    const root = host.shadowRoot;
    if (!root || !root.querySelector('.add-button, .product-card')) return; /* not this widget */

    if (!root.querySelector('style[data-pdp-aftersell]')) {
      const style = document.createElement('style');
      style.setAttribute('data-pdp-aftersell', '');
      style.textContent = AFTERSELL_CSS;
      root.appendChild(style);
    }

    root.querySelectorAll('.product-card').forEach(buildAfterSellPills);

    if (!host.dataset.pdpAftersellWatched) {
      host.dataset.pdpAftersellWatched = '1';
      /* the widget rebuilds itself on variant change and carousel paging, dropping anything added here.
         The guard keeps my own insertions from re-triggering the rebuild. */
      let applying = false;
      new MutationObserver(() => {
        if (applying) return;
        applying = true;
        try { styleAfterSellWidgets(); } finally { window.setTimeout(() => { applying = false; }, 0); }
      }).observe(root, { childList: true, subtree: true });
    }
  });
}

/* it mounts asynchronously, so look for it for the first half-minute and then stop */
let afterSellTries = 0;
const afterSellTimer = window.setInterval(() => {
  afterSellTries += 1;
  styleAfterSellWidgets();
  if (afterSellTries > 40) window.clearInterval(afterSellTimer);
}, 750);

window.pdpStyleAfterSell = styleAfterSellWidgets;

function bootCrossSells(scope) {
  (scope || document).querySelectorAll('[data-pdp-xsell]').forEach((root) => {
    if (!root.dataset.xsellReady) {
      root.dataset.xsellReady = '1';
      new PdpCrossSell(root);
    }
  });
}

document.querySelectorAll('[data-pdp]').forEach((root) => {
  if (!root.dataset.pdpReady) {
    root.dataset.pdpReady = '1';
    new PdpBuyColumn(root);
  }
});

/* each family row on its own: a failure here must never cost the rest of the page, and the tiles still work as links */
document.querySelectorAll('[data-pdp-family]').forEach((root) => {
  if (root.dataset.familyReady) return;
  root.dataset.familyReady = '1';
  try { new PdpFamily(root); } catch (e) { /* links only */ }
});

bootCrossSells(document);

/* complementary mode: the theme's <product-recommendations> fills itself asynchronously;
   boot the component when its content lands, or fall back to the manual list */
document.querySelectorAll('[data-pdp-xsell-reco]').forEach((reco) => {
  const fallback = reco.parentElement && reco.parentElement.querySelector('[data-pdp-xsell-fallback]');
  let settled = false;
  const settle = () => {
    if (settled) return;
    settled = true;
    observer.disconnect();
    window.clearTimeout(timer);
    if (reco.querySelector('[data-pdp-xsell]')) bootCrossSells(reco);
    else if (fallback) { fallback.hidden = false; bootCrossSells(fallback); }
  };
  const observer = new MutationObserver(() => { if (reco.querySelector('[data-pdp-xsell]')) settle(); });
  observer.observe(reco, { childList: true, subtree: true });
  const timer = window.setTimeout(settle, 6000);
});
